import axios from 'axios';

class AuthUtil {
  static getToken() {
    return localStorage.getItem('token');
  }

  static isTokenValid() {
    const token = this.getToken();
    return !!token;
  }

  // Returns the companyId the user is currently operating in. Set by
  // CompanyPicker on every switch. The backend's LockGateInterceptor and
  // AccountService validate this against the user's allowed list, so a
  // tampered value can't reach a company the user isn't linked to —
  // worst case it falls back to the user's home company.
  static getSelectedCompanyId() {
    return localStorage.getItem('selectedCompanyId') || '';
  }

  // Header bundle injected into every authenticated request so the
  // backend evaluates the correct company for lock state, license
  // checks, and any other per-company gating. Empty string → header
  // omitted so the server falls back to the home company.
  static authHeaders(extra = {}) {
    const selectedCompanyId = this.getSelectedCompanyId();
    const headers = {
      Authorization: `Bearer ${this.getToken()}`,
      ...extra,
    };
    if (selectedCompanyId) {
      headers['X-Selected-Company'] = selectedCompanyId;
    }
    return headers;
  }

  static handleLogout() {
    localStorage.clear();
    window.location.replace('/login');
  }

  static async putRequest(endpoint, data) {
    try {
      const response = await axios.put(endpoint, data, {
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      });
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async getRequest(endpoint) {
    try {
      const response = await axios.get(endpoint, {
        headers: this.authHeaders(),
      });
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async postUploadRequest(endpoint, data, onProgress) {
    try {
      const response = await axios.post(endpoint, data, {
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            onProgress(progress);
          }
        },
      });
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async postMultipartRequest(endpoint, formData) {
    try {
      const response = await axios.post(endpoint, formData, {
        headers: this.authHeaders(),
        // Fail hung uploads instead of waiting forever, so callers'
        // retry/outbox logic (e.g. bonus photo queue) can kick in.
        timeout: 120000,
      });
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async postRequest(endpoint, data) {
    try {
      const response = await axios.post(endpoint, data, {
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      });
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static checkAuthError(error) {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      this.handleLogout();
    } else if (error.response && error.response.status === 423) {
      // HTTP 423 LOCKED: server-side LockGateInterceptor refused a
      // feature endpoint because the user's company is locked. This
      // is the FALLBACK path for sessions that didn't receive the
      // 'company-locked' WebSocket broadcast — typically admin-role
      // users whose Base.jsx initConnections skips WebSocket setup.
      //
      // We deliberately do NOT log the user out. Bearer tokens are
      // kept valid for billing-related locks so the user can use the
      // LockedShell's pay/activate flows without re-entering creds.
      // Instead we dispatch a window event that Base.jsx listens for
      // and re-fetches userInfo, which triggers the in-place
      // transition to LockedShell.
      try {
        const payload = error.response.data;
        const lockState = payload && payload.lockState;
        if (lockState && typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('app:locked', { detail: lockState })
          );
        }
      } catch (e) {
        // Best-effort. If the event dispatch fails we still keep
        // the user logged in; the next request will repeat the
        // 423 cycle until the listener fires successfully.
      }
    }
  }
}

export default AuthUtil;
